import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { HISTORY_KEY, INBOX_KEY } from "../queryKeys";

/**
 * Subscribe to `postgres_changes` (INSERT, UPDATE, DELETE) on
 * `public.notifications` for the current user. Invalidates Inbox and
 * History so read/archive/restore from this tab, another device, or a
 * server action all reconcile without the polling window.
 *
 * The channel name is namespaced by user id (`notifs:<uid>`) so running
 * multiple tabs for the same user doesn't collide.
 *
 * No-op when `userId` is falsy — this covers the pre-auth render pass
 * during sign-in where the profile context is still empty.
 */
export function useNotificationRealtime(userId: string | null | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`notifs:${userId}`)
      .on(
        "postgres_changes",
        {
          event:  "*",
          schema: "public",
          table:  "notifications",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          void Promise.all([
            queryClient.invalidateQueries({ queryKey: INBOX_KEY }),
            queryClient.invalidateQueries({ queryKey: HISTORY_KEY }),
          ]);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);
}
